import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { partnerNotesService } from '../../../services/partnerProfileService';

const NotesTab = ({ partnerId, currentUser }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (partnerId) loadNotes();
  }, [partnerId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const data = await partnerNotesService?.getByPartner(partnerId);
      setNotes(data || []);
    } catch (err) {
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote?.trim()) return;
    setSaving(true);
    try {
      const note = await partnerNotesService?.create(partnerId, newNote?.trim());
      setNotes(prev => [note, ...prev]);
      setNewNote('');
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (id) => {
    if (!editContent?.trim()) return;
    try {
      const updated = await partnerNotesService?.update(id, editContent?.trim());
      setNotes(prev => prev?.map(n => n?.id === id ? updated : n));
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await partnerNotesService?.remove(id);
      setNotes(prev => prev?.filter(n => n?.id !== id));
    } catch (err) {
      console.error('Error deleting note:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || '?';
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
      <div className="flex items-center gap-12 mb-24">
        <div className="p-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl">
          <Icon name="StickyNote" size={24} className="text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Internal Notes</h2>
          <p className="text-sm text-slate-600">Admin-only notes — not visible to partners</p>
        </div>
        <div className="ml-auto">
          <span className="px-12 py-6 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold flex items-center gap-6">
            <Icon name="Lock" size={12} />
            Admin Only
          </span>
        </div>
      </div>

      {/* Add Note */}
      <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-20 mb-28">
        <div className="flex items-start gap-12">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-2">
            {getInitials(currentUser?.full_name || currentUser?.email || 'A')}
          </div>
          <div className="flex-1">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e?.target?.value)}
              placeholder="Add an internal note about this partner — relationship context, red flags, strategic notes..."
              rows={3}
              className="w-full px-16 py-12 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none bg-white"
              onKeyDown={(e) => {
                if (e?.key === 'Enter' && (e?.ctrlKey || e?.metaKey)) handleAddNote();
              }}
            />
            <div className="flex items-center justify-between mt-10">
              <span className="text-xs text-slate-400">Ctrl+Enter to submit</span>
              <button
                onClick={handleAddNote}
                disabled={saving || !newNote?.trim()}
                className="px-20 py-10 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-8"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                ) : (
                  <><Icon name="Plus" size={16} /> Add Note</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notes List */}
      {loading ? (
        <div className="flex items-center justify-center py-48">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      ) : notes?.length > 0 ? (
        <div className="space-y-16">
          {notes?.map((note) => (
            <div key={note?.id} className="bg-white rounded-xl border border-slate-200 p-20 hover:shadow-elevation-sm transition-smooth">
              <div className="flex items-start gap-12">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {getInitials(note?.author?.full_name || note?.author?.email || 'A')}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <span className="text-sm font-semibold text-slate-900">
                        {note?.author?.full_name || note?.author?.email || 'Admin'}
                      </span>
                      {note?.author?.role && (
                        <span className="ml-8 px-8 py-2 bg-slate-100 text-slate-600 rounded-full text-xs font-medium capitalize">
                          {note?.author?.role}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-8">
                      <span className="text-xs text-slate-400">{formatDate(note?.created_at)}</span>
                      <button
                        onClick={() => { setEditingId(note?.id); setEditContent(note?.content); }}
                        className="p-6 hover:bg-slate-100 rounded-lg transition-smooth"
                      >
                        <Icon name="Edit2" size={14} className="text-slate-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(note?.id)}
                        disabled={deletingId === note?.id}
                        className="p-6 hover:bg-red-50 rounded-lg transition-smooth"
                      >
                        {deletingId === note?.id ? (
                          <div className="w-3 h-3 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                        ) : (
                          <Icon name="Trash2" size={14} className="text-slate-400 hover:text-red-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {editingId === note?.id ? (
                    <div>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e?.target?.value)}
                        rows={3}
                        className="w-full px-14 py-10 text-sm border border-primary/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                        autoFocus
                      />
                      <div className="flex items-center gap-8 mt-10">
                        <button
                          onClick={() => handleEditSave(note?.id)}
                          className="px-16 py-8 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-smooth"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditContent(''); }}
                          className="px-16 py-8 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-smooth"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{note?.content}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-48">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-16">
            <Icon name="StickyNote" size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-8">No notes yet</h3>
          <p className="text-sm text-slate-600">Add internal notes about this partner for your team.</p>
        </div>
      )}
    </div>
  );
};

export default NotesTab;
